import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PROCESSING_RULE_TYPES } from "@/utils/dataTypes";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { ProcessingRule } from "@/entities/all";
import ProcessingRuleAssistant from "./ProcessingRuleAssistant";
import { toast } from "sonner";

const defaultRuleCode = {
  javascript: `// Processing rule function
// Input: value (the data to process)
// Output: { isValid: boolean, transformedValue: any, message: string }

function process(value) {
  // Add your validation/transformation logic here
  return {
    isValid: true,
    transformedValue: value,
    message: 'Processing successful'
  };
}

return process(input);`,
  python: `# Processing rule function
# Input: value (the data to process)
# Output: { "isValid": bool, "transformedValue": any, "message": str }

def process(value):
    # Add your validation/transformation logic here
    return {
        "isValid": True,
        "transformedValue": value,
        "message": "Processing successful"
    }

return process(input)`,
  sql: `-- SQL processing rule
-- Use {{input}} to reference the input value
SELECT
  CASE
    WHEN {{input}} IS NOT NULL THEN 'valid'
    ELSE 'invalid'
  END as result`,
  tiva_endpoint: `// Tiva endpoint reference
// Specify the endpoint name to call
endpoint: "your.endpoint.name"
// Input will be passed as payload`
};

export default function CreateProcessingRuleModal({ isOpen, onClose, onRuleCreated }) {
  // Which way the user is authoring. 'assistant' is the DEFAULT: the manual
  // form pre-fills a JavaScript template ending in `return process(input)`,
  // which the pyarrow engine silently rejects ("Invalid result format") on
  // every row — so hand-authoring is the expert path, not the first one shown.
  const [mode, setMode] = useState('assistant');
  const [formData, setFormData] = useState({
    rule_name: '',
    description: '',
    rule_code: defaultRuleCode.javascript,
    rule_language: 'javascript',
    rule_type: 'validation',
    input_type: 'string',
    output_type: 'struct',
    status: 'Active',
    version: 1
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await ProcessingRule.create(formData);
      toast.success('Processing rule created successfully');
      onRuleCreated();
      // Reset form
      setFormData({
        rule_name: '',
        description: '',
        rule_code: defaultRuleCode.javascript,
        rule_language: 'javascript',
        rule_type: 'validation',
        input_type: 'string',
        output_type: 'object',
        status: 'Active',
        version: 1
      });
      setMode('assistant');
    } catch (error) {
      console.error('Error creating processing rule:', error);
      toast.error('Failed to create processing rule');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      // Update default code template when language changes
      if (field === 'rule_language' && !prev.rule_code.trim()) {
        newData.rule_code = defaultRuleCode[value] || '';
      }
      return newData;
    });
  };

  const handleLanguageChange = (value) => {
    setFormData(prev => ({
      ...prev,
      rule_language: value,
      rule_code: defaultRuleCode[value] || ''
    }));
  };

  // A drafted rule fills the form and switches to it, so the user reviews the
  // real fields (and can still edit them) before Create. The draft is NOT
  // submitted on apply — Create remains an explicit second action.
  const applyDraft = (draft) => {
    if (!draft) return;
    setFormData(prev => ({
      ...prev,
      rule_name: draft.rule_name || prev.rule_name,
      description: draft.description || prev.description,
      rule_code: draft.rule_code || prev.rule_code,
      rule_language: draft.rule_language || prev.rule_language,
      rule_type: draft.rule_type || prev.rule_type,
      input_type: draft.input_type || prev.input_type,
      test_cases: draft.test_cases?.length ? draft.test_cases : prev.test_cases,
    }));
    setMode('manual');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create New Processing Rule</DialogTitle>
          <DialogDescription>
            Create a reusable processing rule for data validation or transformation.
          </DialogDescription>
        </DialogHeader>

        {/* Authoring mode. Two ways in, not two kinds of rule: whichever is
            used, the same fields get filled and the same Create runs. */}
        <div className="flex items-center gap-0 border-b mt-4">
          {[
            { value: 'assistant', label: 'Describe it' },
            { value: 'manual',    label: 'Write it myself' },
          ].map(({ value, label }) => (
            <button
              key={value}
              type="button"
              onClick={() => setMode(value)}
              data-testid={`create-rule-mode-${value}`}
              className={`h-9 px-4 text-sm border-b-2 cursor-pointer bg-transparent border-t-0 border-l-0 border-r-0 transition-colors ${mode === value ? 'border-nav-active text-nav-active font-medium' : 'border-transparent text-page-muted font-normal'}`}
            >
              {label}
            </button>
          ))}
        </div>

        {mode === 'assistant' && (
          <div className="mt-4">
            <ProcessingRuleAssistant current={null} context={null} onApply={applyDraft} />
          </div>
        )}

        <form
          onSubmit={handleSubmit}
          className="space-y-6 mt-4"
          style={mode === 'assistant' ? { display: 'none' } : undefined}
        >
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label htmlFor="rule_name">Rule Name</Label>
              <Input
                id="rule_name"
                value={formData.rule_name}
                onChange={(e) => handleChange('rule_name', e.target.value)}

                required
              />
              <p className="text-xs text-slate-500 mt-1">Use a unique, descriptive name (e.g., EntityType_Operation)</p>
            </div>

            <div className="col-span-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Describe what this rule does..."
                rows={2}
              />
            </div>

            <div>
              <Label htmlFor="rule_type">Rule Type</Label>
              <Select value={formData.rule_type} onValueChange={(value) => handleChange('rule_type', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="validation">Validation</SelectItem>
                  <SelectItem value="transformation">Transformation</SelectItem>
                  <SelectItem value="normalization">Normalization</SelectItem>
                  <SelectItem value="enrichment">Enrichment</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="rule_language">Language</Label>
              <Select value={formData.rule_language} onValueChange={handleLanguageChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select language" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="javascript">JavaScript</SelectItem>
                  <SelectItem value="python">Python</SelectItem>
                  <SelectItem value="sql">SQL</SelectItem>
                  <SelectItem value="tiva_endpoint">Tiva Endpoint</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="input_type">Input Type</Label>
              <Select value={formData.input_type} onValueChange={(value) => handleChange('input_type', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select input type" />
                </SelectTrigger>
                <SelectContent>
                  {PROCESSING_RULE_TYPES.map(t => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="status">Status</Label>
              <Select value={formData.status} onValueChange={(value) => handleChange('status', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Draft">Draft</SelectItem>
                  <SelectItem value="Deprecated">Deprecated</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="col-span-2">
              <Label htmlFor="rule_code">Rule Code</Label>
              <Textarea
                id="rule_code"
                value={formData.rule_code}
                onChange={(e) => handleChange('rule_code', e.target.value)}
                placeholder="Enter your processing logic code..."
                className="font-mono text-sm"
                rows={12}
                required
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Create Rule'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
